import axios from 'axios';

const API_MODE = process.env.REACT_APP_API_MODE || 'server';
const LOCAL_API_URL = process.env.REACT_APP_API_URL_LOCAL || 'http://localhost:8000';
const SERVER_API_URL = process.env.REACT_APP_API_URL_SERVER || 'https://cacrmapi.freesslforme.com';

const BASE_URL = API_MODE === 'server' ? SERVER_API_URL : LOCAL_API_URL;
const AUTH_STORAGE_KEYS = ['access_token', 'refresh_token', 'user'];

const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const storeAuthTokens = ({ access, refresh }) => {
  if (access) {
    localStorage.setItem('access_token', access);
  }
  if (refresh) {
    localStorage.setItem('refresh_token', refresh);
  }
};

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach Bearer token ─────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: auto-refresh on 401 ────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// ── Rate-limit handling: retry 429s with backoff instead of silently failing ──
const RATE_LIMIT_MAX_RETRIES = 3;
const RATE_LIMIT_DEFAULT_DELAY_MS = 2000;
export const RATE_LIMIT_EVENT = 'api:rate-limited';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getRetryDelayMs = (error) => {
  const header = error.response?.headers?.['retry-after'];
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;
  return RATE_LIMIT_DEFAULT_DELAY_MS;
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const orig = error.config;

    if (error.response?.status === 429 && orig) {
      const attempt = (orig._rateLimitRetryCount || 0) + 1;
      orig._rateLimitRetryCount = attempt;
      if (attempt <= RATE_LIMIT_MAX_RETRIES) {
        await delay(getRetryDelayMs(error));
        return api(orig);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(RATE_LIMIT_EVENT));
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !orig._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
          .then((token) => { orig.headers.Authorization = `Bearer ${token}`; return api(orig); });
      }
      orig._retry = true;
      isRefreshing = true;
      const refresh = localStorage.getItem('refresh_token');
      if (!refresh) { clearAuthStorage(); window.location.href = '/login'; return Promise.reject(error); }
      try {
        const { data } = await axios.post(`${BASE_URL}/api/token/refresh/`, { refresh });
        storeAuthTokens({ access: data.access, refresh: data.refresh });
        api.defaults.headers.common.Authorization = `Bearer ${data.access}`;
        processQueue(null, data.access);
        orig.headers = orig.headers || {};
        orig.headers.Authorization = `Bearer ${data.access}`;
        return api(orig);
      } catch (err) {
        processQueue(err, null);
        clearAuthStorage();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally { isRefreshing = false; }
    }
    return Promise.reject(error);
  }
);

// ════════════════════════════════════════════════════════════════════════════
// AUTH
// POST /api/login/          { email, password } → { access, refresh, role, name, email, phone_number, message }
// POST /api/logout/         { refresh, access }
// POST /api/register/ca/    { first_name, last_name, email, password, ... }
// POST /api/change-password/ { old_password, new_password, confirm_password }
// POST /api/token/refresh/  { refresh } → { access }
// ════════════════════════════════════════════════════════════════════════════
export const authAPI = {
  login: (email, password) => api.post('/api/login/', { email, password }),
  logout: () => api.post('/api/logout/', {
    refresh: localStorage.getItem('refresh_token'),
    access: localStorage.getItem('access_token'),
  }),
  completeTour: () => api.post('/api/tour-completed/'),
  registerCA: (data) => api.post('/api/register/ca/', data),
  changePassword: (old_password, new_password, confirm_password) =>
    api.post('/api/change-password/', { old_password, new_password, confirm_password }),
};

// ════════════════════════════════════════════════════════════════════════════
// USERS  /api/users/
// GET    /api/users/                 CA→all active, AM→own CLNTs only
//   params: page, search, full_name, email, role, uid, show_deleted, ordering
// POST   /api/users/                 { first_name, last_name, middle_name, email, password, role, phone_number, ... }
//   response: { message, data: UserObject }
// GET    /api/users/{id}/
// PATCH  /api/users/{id}/            partial update
// DELETE /api/users/{id}/            soft-delete (is_active=false)  → 204
// POST   /api/users/{id}/restore/    restore → { message, data }
// PATCH  /api/users/bulk_update/     [{ id, data:{...} }, ...]
// DELETE /api/users/bulk_delete/     [id, id, ...]
//
// UserObject fields: id, uid, full_name, first_name, middle_name, last_name,
//   email, role, phone_number, country, state, city, address, pincode,
//   date_of_birth, assigned_to, assigned_to_name, created_at, updated_at
// ════════════════════════════════════════════════════════════════════════════
export const userAPI = {
  list: (params = {}) => api.get('/api/users/', { params }),
  get: (id, params = {}) => api.get(`/api/users/${id}/`, { params }),
  create: (data) => api.post('/api/users/', data, data instanceof FormData ? {
    headers: { 'Content-Type': 'multipart/form-data' },
  } : undefined),
  update: (id, data) => api.patch(`/api/users/${id}/`, data, data instanceof FormData ? {
    headers: { 'Content-Type': 'multipart/form-data' },
  } : undefined),
  delete: (id) => api.delete(`/api/users/${id}/`),
  restore: (id) => api.post(`/api/users/${id}/restore/`),
  bulkUpdate: (items) => api.patch('/api/users/bulk_update/', items),
  bulkDelete: (ids) => api.delete('/api/users/bulk_delete/', { data: ids }),

  // POST /api/assign-client/                { client_id, account_manager_id }
  assignClient: (client_id, account_manager_id) =>
    api.post('/api/assign-client/', { client_id, account_manager_id }),

  // GET /api/assign-client/{am_id}/         → { id, username, clients:[{id,full_name,email}] }
  getClientsByAM: (account_manager_id) =>
    api.get(`/api/assign-client/${account_manager_id}/`),

  // POST /api/remove-assign-client/         { client_id, account_manager_id }
  removeClientFromAM: (client_id, account_manager_id) =>
    api.post('/api/remove-assign-client/', { client_id, account_manager_id }),
};

// ════════════════════════════════════════════════════════════════════════════
// PERMISSIONS  /api/permissions/
// GET  /api/permissions/          params: granted_to_id, permission_type
//   response: { data: [PermissionObject] }
// POST /api/permissions/          { granted_to_id, permission_types: ['create_user', ...] }
//   response: { message, newly_granted_permissions }
// DELETE /api/permissions/remove/ body: { granted_to_id, permission_types: [...] }
//   response: { message, removed_permissions }
//
// PERMISSION_CHOICES: create_user, update_user, delete_user, restore_user,
//   create_company, update_company, delete_company, restore_company,
//   create_document, update_document, delete_document, restore_document,
//   create_comment, update_comment, delete_comment, restore_comment
// ════════════════════════════════════════════════════════════════════════════
export const permissionAPI = {
  list: (params = {}) => api.get('/api/permissions/', { params }),
  grant: (granted_to_id, permission_types) =>
    api.post('/api/permissions/', { granted_to_id, permission_types }),
  revoke: (granted_to_id, permission_types) =>
    api.delete('/api/permissions/remove/', { data: { granted_to_id, permission_types } }),
};

export const PERMISSION_CHOICES = [
  'create_user', 'update_user', 'delete_user', 'restore_user',
  'create_company', 'update_company', 'delete_company', 'restore_company',
  'create_document', 'update_document', 'delete_document', 'restore_document',
  'create_comment', 'update_comment', 'delete_comment', 'restore_comment',
  'create_task', 'update_task', 'delete_task', 'restore_task',
];

// ════════════════════════════════════════════════════════════════════════════
// COMPANIES  /api/companies/
// GET    /api/companies/               CA→all, AM→assigned ones, CLNT→own
//   params: page, search, name, registration_number, gst_number, cid, show_deleted, ordering
// POST   /api/companies/               { name, registration_number, gst_number, address, city,
//                                        state, country, pincode, phone_number, email, website,
//                                        created_by, client, account_manager }
//   response: { message, data: CompanyObject }
// GET    /api/companies/{id}/
// PATCH  /api/companies/{id}/          partial update
// DELETE /api/companies/{id}/          soft-delete → 204
// POST   /api/companies/{id}/restore/
// PATCH  /api/companies/bulk_update/   [{ id, data:{...} }]
// DELETE /api/companies/bulk_delete/   [id, id, ...]
//
// POST /api/assign-companies/             { account_manager_id, company_ids:[] }
// GET  /api/assign-companies/{am_id}/     → { id, name, companies:[CompanyObject] }
// POST /api/remove-companies-from-am/     { account_manager_id, company_ids:[] }
// POST /api/remove-companies-from-client/ { client_id, company_ids:[] }
// GET  /api/client-companies-documents/   ?client_id=X  → [CompanyWithDocs]
// GET  /api/client-companies/            ?client_id=X  → [{id, name}]
// GET  /api/companies-details/           ?company_id=X → CompanyWithDocsAndComments
//
// CompanyObject fields: id, cid, name, registration_number, gst_number,
//   address, city, state, country, pincode, phone_number, email, website,
//   created_by, client, account_manager, client_name, account_manager_name,
//   created_at, updated_at
// ════════════════════════════════════════════════════════════════════════════
export const companyAPI = {
  list: (params = {}) => api.get('/api/companies/', { params }),
  get: (id) => api.get(`/api/companies/${id}/`),
  create: (data) => api.post('/api/companies/', data),
  update: (id, data) => api.patch(`/api/companies/${id}/`, data),
  delete: (id) => api.delete(`/api/companies/${id}/`),
  restore: (id) => api.post(`/api/companies/${id}/restore/`),
  bulkUpdate: (items) => api.patch('/api/companies/bulk_update/', items),
  bulkDelete: (ids) => api.delete('/api/companies/bulk_delete/', { data: ids }),

  assignToAM: (account_manager_id, company_ids, categories = []) =>
    api.post('/api/assign-companies/', { account_manager_id, company_ids, categories }),
  getByAM: (account_manager_id) =>
    api.get(`/api/assign-companies/${account_manager_id}/`),
  removeFromAM: (account_manager_id, company_ids) =>
    api.post('/api/remove-companies-from-am/', { account_manager_id, company_ids }),
  removeFromClient: (client_id, company_ids) =>
    api.post('/api/remove-companies-from-client/', { client_id, company_ids }),
  getClientCompaniesWithDocs: (client_id) =>
    api.get('/api/client-companies-documents/', { params: { client_id } }),
  getClientCompaniesList: (client_id) =>
    api.get('/api/client-companies/', { params: { client_id } }),
  getDetails: (company_id) =>
    api.get('/api/companies-details/', { params: { company_id } }),
};

// ════════════════════════════════════════════════════════════════════════════
// DOCUMENTS  /api/documents/
// GET    /api/documents/              list
//   filter: company (id), category, client (id), document_year, from_date (YYYY-MM-DD), to_date (YYYY-MM-DD)
//   search: company__name, category, document_year
//   ordering: uploaded_at, category, document_year
//   show_deleted supported
// POST   /api/documents/              multipart/form-data
//   fields: company (id), category, document_year, documents (multiple files, key="documents")
//   response: { message, data: [DocumentObject] }
// GET    /api/documents/{id}/
// PATCH  /api/documents/{id}/
// DELETE /api/documents/{id}/         → 204
// POST   /api/documents/{id}/restore/
// DELETE /api/documents/bulk_delete/  [id, id, ...]
//
// DocumentObject fields: id, company, file, uploaded_at, category, document_year
// ════════════════════════════════════════════════════════════════════════════
export const documentAPI = {
  list: (params = {}) => api.get('/api/documents/', { params }),
  get: (id) => api.get(`/api/documents/${id}/`),
  countsByCompany: (companyIds, params = {}) =>
    api.get('/api/documents/counts-by-company/', { params: { ...params, company_ids: companyIds.join(',') } }),
  upload: (company_id, category, files, document_year = '') => {
    const fd = new FormData();
    fd.append('company', company_id);
    fd.append('category', category);
    if (document_year) fd.append('document_year', document_year);
    files.forEach((f) => fd.append('documents', f));
    return api.post('/api/documents/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  update: (id, data) => api.patch(`/api/documents/${id}/`, data),
  delete: (id) => api.delete(`/api/documents/${id}/`),
  restore: (id) => api.post(`/api/documents/${id}/restore/`),
  bulkDelete: (ids) => api.delete('/api/documents/bulk_delete/', { data: ids }),
};

export const taskAPI = {
  list: (params = {}) => api.get('/api/tasks/', { params }),
  get: (id, params = {}) => api.get(`/api/tasks/${id}/`, { params }),
  create: (data) => api.post('/api/tasks/', data),
  update: (id, data) => api.patch(`/api/tasks/${id}/`, data),
  delete: (id) => api.delete(`/api/tasks/${id}/`),
  restore: (id) => api.post(`/api/tasks/${id}/restore/`),
  transfer: (id, assigned_to) => api.post(`/api/tasks/${id}/transfer/`, { assigned_to }),
  getByAM: (am_id) => api.get(`/api/tasks/am/${am_id}/`),
  addComment: (taskId, content) => api.post(`/api/tasks/${taskId}/add-comment/`, { content }),
  editComment: (taskId, commentId, content) => api.patch(`/api/tasks/${taskId}/edit-comment/${commentId}/`, { content }),
  uploadDocument: (taskId, formData) => api.post(`/api/tasks/${taskId}/upload-document/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getNotificationCount: () => api.get('/api/tasks/notification-count/'),
  markTaskRead: (taskId) => api.post(`/api/tasks/${taskId}/mark-read/`),
  markAllTasksRead: () => api.post('/api/tasks/mark-all-read/'),
  deleteDocument: (taskId, docId) => api.delete(`/api/tasks/${taskId}/delete-document/${docId}/`),
  restoreDocument: (taskId, docId) => api.post(`/api/tasks/${taskId}/restore-document/${docId}/`),
};

// ════════════════════════════════════════════════════════════════════════════
// DOCUMENT CATEGORIES  /api/document-categories/
// GET    /api/document-categories/          list (all authenticated users)
// POST   /api/document-categories/          { name }  (CA only)
// PATCH  /api/document-categories/{id}/     { name }  (CA only)
// DELETE /api/document-categories/{id}/               (CA only)
// ════════════════════════════════════════════════════════════════════════════
export const categoryAPI = {
  list: () => api.get('/api/document-categories/'),
  create: (name) => api.post('/api/document-categories/', { name }),
  update: (id, name) => api.patch(`/api/document-categories/${id}/`, { name }),
  delete: (id) => api.delete(`/api/document-categories/${id}/`),
};

export const DOCUMENT_CATEGORIES = [
  'Accounting',
  'Audit',
  'Bank Statements',
  'Banking / Finance',
  'Certificate',
  'Company Master',
  'DSC',
  'FSSAI Regn.',
  'GST',
  'HUF Regn.',
  'IEC',
  'Imports / Exports',
  'Insurance',
  'Invoices / Billing',
  'ITR',
  'Legal / Agreements',
  'Licenses / Registrations',
  'MSME Regn.',
  'Notices / Litigation',
  'Other',
  'Payroll',
  'PF / ESIC',
  'Professional Tax',
  'ROC / MCA',
  'Shop&Estab Regn',
  'Startup Regn',
  'TDS / TCS',
  'Trademark Regn',
];

const buildDocumentYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 10;
  const endYear = Math.max(currentYear + 2, 2030);
  const options = Array.from({ length: endYear - startYear + 1 }, (_, index) => {
    const year = endYear - index;
    const prefix = year >= 2027 ? 'TY' : 'AY';
    return `${prefix} ${year}-${String(year + 1).slice(-2)}`;
  });
  const ayIdx = options.indexOf('AY 2026-27');
  if (ayIdx !== -1) options.splice(ayIdx, 0, 'TY 2026-27');
  return options;
};

export const DOCUMENT_YEAR_OPTIONS = buildDocumentYearOptions();

// ════════════════════════════════════════════════════════════════════════════
// COMMENTS  /api/comments/
// GET    /api/comments/              list
//   filter: user (id), company (id)
//   search: user__full_name, content
//   ordering: created_at, updated_at
//   show_deleted supported
// POST   /api/comments/              { user (id), content, company (id) }
//   response: { message, data: CommentObject }
// GET    /api/comments/{id}/
// PATCH  /api/comments/{id}/         { content } (partial)
// DELETE /api/comments/{id}/         soft-delete → 204
// POST   /api/comments/{id}/restore/
// PATCH  /api/comments/bulk_update/  [{ id, data:{content} }]
// DELETE /api/comments/bulk_delete/  [id, id, ...]
//
// CommentObject fields: id, user, content, created_at, updated_at, company
// ════════════════════════════════════════════════════════════════════════════
export const commentAPI = {
  list: (params = {}) => api.get('/api/comments/', { params }),
  get: (id) => api.get(`/api/comments/${id}/`),
  create: (user_id, content, company_id, account_manager_id = null) =>
    api.post('/api/comments/', { user: user_id, content, company: company_id, ...(account_manager_id ? { account_manager: account_manager_id } : {}) }),
  update: (id, content) => api.patch(`/api/comments/${id}/`, { content }),
  delete: (id) => api.delete(`/api/comments/${id}/`),
  restore: (id) => api.post(`/api/comments/${id}/restore/`),
  bulkUpdate: (items) => api.patch('/api/comments/bulk_update/', items),
  bulkDelete: (ids) => api.delete('/api/comments/bulk_delete/', { data: ids }),
};

// ════════════════════════════════════════════════════════════════════════════
// DIRECT CHAT  /api/direct-messages/   (CA ↔ AM only)
// GET  /api/direct-messages/?with=<user_id>   messages with a specific user
// POST /api/direct-messages/                  { receiver, content, file? }
// POST /api/direct-messages/mark-read/        { with: user_id }
// GET  /api/direct-messages/threads/          list of conversation threads
// ════════════════════════════════════════════════════════════════════════════
export const chatAPI = {
  contacts: () => api.get('/api/direct-messages/contacts/'),
  threads: () => api.get('/api/direct-messages/threads/'),
  messages: (withUserId) => api.get('/api/direct-messages/', { params: { with: withUserId } }),
  send: (receiverId, content, file = null) => {
    if (file) {
      const fd = new FormData();
      fd.append('receiver', receiverId);
      fd.append('content', content);
      fd.append('file', file);
      return api.post('/api/direct-messages/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/api/direct-messages/', { receiver: receiverId, content });
  },
  markRead: (withUserId) => api.post('/api/direct-messages/mark-read/', { with: withUserId }),
  edit: (messageId, content) => api.patch(`/api/direct-messages/${messageId}/`, { content }),
};

export default api;
